import { BookingOption, Option } from '@jooxter/api';
import { BOOKING_OPTION_PREFIX, IQuickTimeSlot, IRange, isSameDay } from '@jooxter/utils';
import { JxtAttendeeOption, JxtAttendeeOptionTypeEnum } from '../../../components/JxtAttendeeCombobox/types';
import { IGetInitialAttendees } from './types';

export const getDefaultOptions = (resourceOptions?: Option[] | null, bookingOptions?: BookingOption[] | null) => {
  if (!resourceOptions?.length) {
    return;
  }

  return Object.fromEntries(
    resourceOptions.map((option) => {
      const bookingOption = bookingOptions?.find((o) => o.optionId === option.id);

      if (bookingOption) {
        return [
          `${BOOKING_OPTION_PREFIX}-${bookingOption.optionId}`,
          {
            activated: true,
            value: bookingOption.value?.content,
          },
        ];
      }

      return [
        `${BOOKING_OPTION_PREFIX}-${option.id}`,
        {
          activated: false,
          value: option.defaultValue,
        },
      ];
    })
  );
};

/**
 * @returns {boolean} true if at least one slot is not on the same day as the range
 */
export const isOutOfSync = (slots: IQuickTimeSlot[], range: IRange, allowDifferentDays = false): boolean => {
  // normalize seconds
  const normalizedRange: IRange = {
    from: range.from.startOf('minute'),
    to: range.to.startOf('minute'),
  };

  if (allowDifferentDays) {
    return slots.some((s) => !isSameDay(s.start, normalizedRange.from));
  }

  return slots.some((s) => !isSameDay(s.start, normalizedRange.from) || !isSameDay(s.end, normalizedRange.to));
};

/**
 * The attendee chips the form opens with. Attendee ids live in the form (`internalAttendees` /
 * `externalAttendees`); this only builds the labels to display next to them, which is why it is a
 * one-shot computation seeding local state rather than something derived on every render.
 */
export const getInitialAttendees = ({
  bookingToEdit,
  moveFormOverride,
  isZone,
  user,
  organizerInPreset,
  youLabel,
}: IGetInitialAttendees): JxtAttendeeOption[] => {
  // Pivoting from the overlap-resolution "move to new resource" choice: reuse the chips the user
  // had just filled in, not the conflicting booking's own attendees.
  if (moveFormOverride) {
    return moveFormOverride.attendees;
  }

  if (bookingToEdit) {
    return [
      ...bookingToEdit.attendees.internal.map((attendee) => ({
        type: JxtAttendeeOptionTypeEnum.INTERNAL,
        id: attendee.id,
        text: attendee.name + (attendee.id === user?.id ? ` (${youLabel})` : ''),
      })),
      ...bookingToEdit.attendees.external.map((attendee) => ({
        type: JxtAttendeeOptionTypeEnum.EXTERNAL,
        id: attendee.id,
        text: attendee.email,
      })),
    ];
  }

  // A zone booking always counts its organizer in.
  if (isZone && user) {
    const organizer = organizerInPreset ?? user;

    return [
      {
        type: JxtAttendeeOptionTypeEnum.INTERNAL,
        id: organizer.id,
        text: `${organizer.firstname} ${organizer.lastname} ${organizer.id === user.id ? `(${youLabel})` : ''}`,
      },
    ];
  }

  return [];
};
